import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'src/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

/**
 * A one-line, ellipsis-truncated description cell. The rich tooltip is only
 * wired up when the text is actually clipped — short descriptions stay plain,
 * with no needless hover affordance (mirrors the shared `markClipped` logic).
 */
export const DescriptionCell = ({ value, label, maxWidthClass }) => {
  const ref = useRef(null);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (el) setClipped(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value]);

  if (!value) {
    return (
      <span className="block text-[12.5px] italic text-muted-foreground">
        —
      </span>
    );
  }

  const clip = (
    <span
      ref={ref}
      // When clipped, this span is the tooltip trigger and must be
      // keyboard-focusable so the full text is reachable without a mouse.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={clipped ? 0 : undefined}
      className={cn(
        'block cursor-default truncate text-[12.5px] text-muted-foreground',
        maxWidthClass,
        // Clipped descriptions carry the tooltip — light-blue highlight on
        // hover/focus signals "there's more to read". Negative margin + padding
        // keeps the text aligned so the row doesn't shift.
        clipped &&
          '-mx-1 rounded-[5px] px-1 transition-colors hover:bg-primary/10 hover:text-primary focus-visible:bg-primary/10 focus-visible:text-primary focus-visible:outline-none'
      )}
    >
      {value}
    </span>
  );

  if (!clipped) return clip;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{clip}</TooltipTrigger>
        <TooltipContent name={label} side="top" align="start">
          {value}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

DescriptionCell.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string,
  maxWidthClass: PropTypes.string,
};

DescriptionCell.defaultProps = {
  value: '',
  label: undefined,
  maxWidthClass: 'max-w-[44ch]',
};
